import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import {
  DeleteVoicemailResponse,
  MailBoxType,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { UseMutateFunction } from 'react-query';
import { VoicemailApi, VoicemailTypes } from '@frontend/api-voicemails';
import { useTranslation } from '@frontend/i18n';
import { CachedAudioScrubber } from '@frontend/phone-panels';
import { useAppScopeStore } from '@frontend/scope';
import {
  Modal,
  Text,
  ModalControlModalProps,
  useControlledField,
  DropdownField,
  CheckboxField,
  useAlert,
  NumberField,
  useModalControl,
} from '@frontend/design-system';
import { ForwardVoicemailDynamicData, ForwardVoicemailModalData } from './voicemails-multi.component';

type Props = {
  modalProps: ModalControlModalProps;
  forwardData: ForwardVoicemailModalData;
  onForwardSuccess?: () => void;
  queryKey?: string[];
};

const otherDestMBBox = 'other';

const getMailboxDetails = (mailbox: VoicemailBox) => {
  const { mailbox: mailboxData } = mailbox;
  return {
    name: mailboxData?.name ?? '--',
    id: mailboxData?.id,
  };
};

const getDropdownListOption = ({
  mailbox,
  personalMailboxId,
  currentMailBoxId,
}: {
  mailbox: VoicemailBox;
  personalMailboxId: string;
  currentMailBoxId: string;
}) => {
  const { id, name } = getMailboxDetails(mailbox);
  if (id === personalMailboxId) {
    return null;
  }
  return (
    <DropdownField.Option key={id ?? ''} value={id ?? ''} disabled={currentMailBoxId == id}>
      {name}
    </DropdownField.Option>
  );
};

const mapVoicemailBoxesToLocation = ({
  voicemailBoxes,
  allLocations,
}: {
  voicemailBoxes: VoicemailBox[];
  allLocations: string[];
}): Record<string, VoicemailBox[]> => {
  const locationToVoicemailBoxesMap: Record<string, VoicemailBox[]> = {};
  voicemailBoxes?.forEach((box) => {
    box.locationIds?.forEach((locationId) => {
      // we only want to show the voicemail boxes that are in the child locations
      const isChildLocation = allLocations.some((locId) => locationId === locId);
      if (!isChildLocation) {
        return;
      }
      if (!locationToVoicemailBoxesMap[locationId]) {
        locationToVoicemailBoxesMap[locationId] = [];
      }
      locationToVoicemailBoxesMap[locationId].push(box);
    });
  });
  return locationToVoicemailBoxesMap;
};

export const useForwardVoicemailModalControl = ({
  voicemailBoxList,
  onDelete,
}: {
  voicemailBoxList?: VoicemailBox[];
  onDelete: UseMutateFunction<DeleteVoicemailResponse, unknown, VoicemailTypes.DeletePayload, void>;
}) => {
  const [forwardVoicemailModalData, setForwardVoicemailModalData] = useState<ForwardVoicemailModalData>(
    {} as ForwardVoicemailModalData
  );
  const forwardModalControl = useModalControl();

  const openForwardVoicemailModal = (data: ForwardVoicemailDynamicData) => {
    setForwardVoicemailModalData({ ...data, voicemailBoxList: voicemailBoxList || [], onDelete: onDelete });
    forwardModalControl.openModal();
  };

  return {
    forwardVoicemailModalData,
    openForwardVoicemailModal,
    forwardVoicemailModalProps: forwardModalControl.modalProps,
  };
};

export const Forwardvoicemail = ({ modalProps, forwardData, onForwardSuccess }: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('calls', { keyPrefix: 'voicemails' });

  const { selectedLocationIds, getScopeName } = useAppScopeStore();

  const personalMailboxData =
    forwardData.voicemailBoxList?.find((mailbox) => mailbox.mailboxType === MailBoxType.MAILBOX_TYPE_PERSONAL) ?? null;

  const otherMailboxList = forwardData.voicemailBoxList?.filter(
    (box) => box.mailboxType !== MailBoxType.MAILBOX_TYPE_PERSONAL
  );

  const [selectedMailboxIdToForward, setSelectedMailboxIdToForward] = useState<string>('');
  const [forwardMailboxExtn, setForwardMailboxExtn] = useState<string>('');
  const [deleteAfterForward, setDeleteAfterForward] = useState<boolean>(false);

  useEffect(() => {
    // reset input modal input data whenever the voicemail id is changed
    setSelectedMailboxIdToForward('');
    setDeleteAfterForward(false);
  }, [forwardData.vmId]);

  const forwardVoicemailBoxDropdownProps = useControlledField({
    type: 'dropdown',
    required: false,
    onChange: (value: string) => {
      setSelectedMailboxIdToForward(value);
    },
    value: selectedMailboxIdToForward,
  });

  const forwardVoicemailBoxExtentionNumber = useControlledField({
    type: 'number',
    required: false,
    allowDecimal: false,
    allowNegative: false,
    includeThousandsSeparator: false,
    value: forwardMailboxExtn,
    onChange: (value) => {
      setForwardMailboxExtn(value);
    },
  });

  const deleteCurrentVoicemailProps = useControlledField({
    onChange: (value) => {
      setDeleteAfterForward(value);
    },
    type: 'checkbox',
    value: deleteAfterForward,
  });

  const forwardVoicemail = async (
    voicemailId: string,
    fromMailboxId: string,
    toMailboxId: string,
    rowIndex: number
  ) => {
    try {
      if (toMailboxId == otherDestMBBox) {
        toMailboxId = '';
      }
      await VoicemailApi.forwardVoicemail({
        voicemailMessageId: voicemailId,
        originalVoicemailBoxId: fromMailboxId,
        destinationVoicemailBoxId: toMailboxId,
        destinationVoicemailBoxNumber: forwardMailboxExtn,
      });
      alert.success(t('VoicemailForwardedSuccess'));
      if (deleteAfterForward) {
        await forwardData.onDelete({ voicemailId: voicemailId, index: rowIndex, mailboxId: fromMailboxId });
      }
      if (!!onForwardSuccess) {
        onForwardSuccess?.();
      }
    } catch (error) {
      alert.error(t('VoicemailForwardError'));
    }
  };

  const locationNameToIdMap: Record<string, string> = selectedLocationIds.reduce((acc, id) => {
    const name = getScopeName(id);
    acc[name] = id;
    return acc;
  }, {} as Record<string, string>);

  const locationToVoicemailBoxesMap = mapVoicemailBoxesToLocation({
    voicemailBoxes: forwardData.voicemailBoxList,
    allLocations: selectedLocationIds,
  });

  return (
    <>
      <Modal {...modalProps} maxWidth={900}>
        <Modal.Header>{t('Forward Voicemail')}</Modal.Header>
        <Modal.Body>
          <Text
            css={css`
              margin-top: 0;
            `}
          >
            {t('Choose which voicemail box you want to forward this voicemail to')}
          </Text>
          <br />
          <div css={{ display: 'flex', alignItems: 'flex-start', boxSizing: 'border-box', gap: '20px' }}>
            <div css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: '10px' }}>
              <span css={{ fontSize: '12px', color: 'grey' }}>Voicemail</span>
              <CachedAudioScrubber filePath={forwardData.mediaPath} mediaId={forwardData.mediaId} />
            </div>
            <div css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: '18px' }}>
              <span css={{ fontSize: '12px', color: 'grey' }}>{t('Current Voicemail Box')}</span>
              <span>{forwardData.currentVoicemailBoxName}</span>
            </div>
          </div>
          <br />
          <div style={{ maxWidth: '350px' }}>
            <DropdownField
              {...forwardVoicemailBoxDropdownProps}
              label={t('Destination voicemail box')}
              name='dest-vm-box'
            >
              {!!personalMailboxData && (
                <DropdownField.Option
                  value={personalMailboxData?.mailbox?.id ?? ''}
                  disabled={forwardData.currentVoicemailBoxId == personalMailboxData?.mailbox?.id}
                >
                  {personalMailboxData?.mailbox?.name ?? ''}
                </DropdownField.Option>
              )}

              {selectedLocationIds.length === 1 &&
                otherMailboxList?.map((mailbox) =>
                  getDropdownListOption({
                    mailbox,
                    personalMailboxId: personalMailboxData?.mailbox?.id ?? '',
                    currentMailBoxId: forwardData.currentVoicemailBoxId,
                  })
                )}

              {selectedLocationIds.length > 1 &&
                Object.entries(locationNameToIdMap).map(([locationName, locationId]) => {
                  const sortedVoicemailBoxes = locationToVoicemailBoxesMap[locationId]?.filter(
                    (box) => box?.mailbox?.id !== personalMailboxData?.mailbox?.id
                  );

                  return (
                    <DropdownField.OptionGroup key={locationId} label={locationName}>
                      {sortedVoicemailBoxes?.map((mailbox) =>
                        getDropdownListOption({
                          mailbox,
                          personalMailboxId: personalMailboxData?.mailbox?.id ?? '',
                          currentMailBoxId: forwardData.currentVoicemailBoxId,
                        })
                      )}
                    </DropdownField.OptionGroup>
                  );
                })}
              <DropdownField.Option css={{ fontWeight: 'bold' }} key={otherDestMBBox} value={otherDestMBBox}>
                {t('other (specify extension)')}
              </DropdownField.Option>
            </DropdownField>
          </div>
          {selectedMailboxIdToForward == otherDestMBBox && (
            <div style={{ width: '350px' }}>
              <br />
              <NumberField
                {...forwardVoicemailBoxExtentionNumber}
                label={t('Enter the extension number')}
                name='extn-number'
              />
            </div>
          )}

          <br />
          <CheckboxField
            {...deleteCurrentVoicemailProps}
            name='delete'
            label={t('Remove voicemail from current Voicemail Box after forwarding')}
          />
          <br />
        </Modal.Body>
        <Modal.Actions
          onSecondaryClick={() => {
            return true;
          }}
          onPrimaryClick={() => {
            forwardVoicemail(
              forwardData.vmId,
              forwardData.currentVoicemailBoxId,
              selectedMailboxIdToForward ?? '',
              forwardData.rowIndex
            );
            return true;
          }}
          disablePrimary={
            selectedMailboxIdToForward == otherDestMBBox ? forwardMailboxExtn == '' : selectedMailboxIdToForward == ''
          }
          primaryLabel={t('Forward')}
          primaryTrackingId='modal-forward-voicemail'
          secondaryLabel={t('Cancel')}
        />
      </Modal>
    </>
  );
};
